import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  Dialog,
} from '@mui/material';
import {
  Add,
  Search,
  Edit,
  Delete,
  Visibility,
  FilterList,
  Inventory2,
  Warning,
  CheckCircle,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import ProductoForm from './ProductoForm';
import ProductoDetalle from './ProductoDetalle';
import { usePermissions } from '../hooks/usePermissions';

const Productos = () => {
  // Hook de permisos
  const { canWrite, canDelete } = usePermissions();

  const [productos, setProductos] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [selectedProducto, setSelectedProducto] = useState(null);
  const [openForm, setOpenForm] = useState(false);
  const [openDetalle, setOpenDetalle] = useState(false);
  const [editingProducto, setEditingProducto] = useState(null);
  const [filtroEstado, setFiltroEstado] = useState('');
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0,
  });

  // Debounce para búsqueda automática (espera 300ms después de que el usuario deja de escribir)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Resetear a página 1 cuando cambia el término de búsqueda
  useEffect(() => {
    setPagination(prev => ({ ...prev, page: 1 }));
  }, [debouncedSearchTerm]);

  useEffect(() => {
    loadProductos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.page, debouncedSearchTerm, filtroEstado]);

  const loadProductos = async () => {
    try {
      const token = localStorage.getItem('token');

      const params = new URLSearchParams({
        page: pagination.page,
        limit: pagination.limit,
        search: debouncedSearchTerm,
        ...(filtroEstado && { estado: filtroEstado }),
      });

      const response = await fetch(`/api/productos?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        // Mapear campos del backend (inglés) al frontend (español)
        const productosMapeados = data.productos.map(p => ({
          ...p,
          nombre: p.name,
          codigo_sap: p.sapCode,
          descripcion_presentacion: p.description,
          laboratorio_fabricante: p.manufacturer,
          numero_registro_sanitario: p.sanitaryRegistryNumber,
          estado_registro: p.status === 'activo' ? 'vigente' : 'vencido',
          estado: p.status
        }));
        setProductos(productosMapeados);
        setPagination(prev => ({ ...prev, ...data.pagination }));
      }
    } catch (error) {
      console.error('Error loading productos:', error);
    }
  };

  const handleOpenForm = async (producto = null) => {
    if (producto) {
      // Si es edición, cargar datos completos del producto (incluyendo imágenes y documentos)
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`/api/productos/${producto.id}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        if (response.ok) {
          const data = await response.json();
          // Mapear campos del backend (inglés) al frontend (español)
          const productoMapeado = {
            ...data,
            // Campos básicos
            nombre: data.name,
            descripcion: data.description,
            descripcion_presentacion: data.description,
            categoria: data.category,
            estado: data.status,
            codigo_sap: data.sapCode,
            es_descartable: data.isDisposable,
            laboratorio_fabricante: data.manufacturer,
            vida_util: data.shelfLife,
            pais_origen: data.originCountry,
            codigo_proveedor: data.providerCode,
            registro_sanitario_id: data.sanitaryRegistryId,
            // Imágenes
            imagen_1: data.image1,
            imagen_2: data.image2,
            imagen_3: data.image3,
            imagen_4: data.image4,
            // Documentos
            documento_1: data.document1,
            documento_2: data.document2,
            documento_3: data.document3,
            documento_4: data.document4,
            documento_5: data.document5,
            documento_6: data.document6,
            documento_7: data.document7,
            documento_8: data.document8,
            documento_9: data.document9,
            documento_10: data.document10,
            documento_11: data.document11,
            documento_12: data.document12,
            nombre_documento_1: data.documentName1,
            nombre_documento_2: data.documentName2,
            nombre_documento_3: data.documentName3,
            nombre_documento_4: data.documentName4,
            nombre_documento_5: data.documentName5,
            nombre_documento_6: data.documentName6,
            nombre_documento_7: data.documentName7,
            nombre_documento_8: data.documentName8,
            nombre_documento_9: data.documentName9,
            nombre_documento_10: data.documentName10,
            nombre_documento_11: data.documentName11,
            nombre_documento_12: data.documentName12,
          };
          setEditingProducto(productoMapeado);
        }
      } catch (error) {
        console.error('Error loading producto for edit:', error);
      }
    } else {
      setEditingProducto(null);
    }
    setOpenForm(true);
  };

  const handleCloseForm = () => {
    setOpenForm(false);
    setEditingProducto(null);
    // Volver a la página 1 para ver el producto recién creado
    setPagination(prev => ({ ...prev, page: 1 }));
    // No llamar loadProductos() aquí - el useEffect lo hará automáticamente cuando cambie la página
  };

  const handleViewDetalle = async (producto) => {
    try {
      const token = localStorage.getItem('token');

      const response = await fetch(`/api/productos/${producto.id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setSelectedProducto(data);
        setOpenDetalle(true);
      }
    } catch (error) {
      console.error('Error loading producto details:', error);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Estás seguro de eliminar este producto?')) return;

    try {
      const token = localStorage.getItem('token');

      const response = await fetch(`/api/productos/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        loadProductos();
      }
    } catch (error) {
      console.error('Error deleting producto:', error);
    }
  };

  const getEstadoChip = (estado) => {
    const config = {
      activo: { color: 'success', label: 'Activo' },
      inactivo: { color: 'default', label: 'Inactivo' },
      descontinuado: { color: 'error', label: 'Descontinuado' },
    };

    const { color, label } = config[estado] || config.activo;

    return <Chip label={label} color={color} size="small" />;
  };

  return (
    <Box>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
            Gestión de Productos
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Administra el catálogo completo de productos farmacéuticos
          </Typography>
        </Box>

        {/* Estadísticas */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ borderRadius: '16px', bgcolor: 'rgba(33, 150, 243, 0.1)' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: '#2196f3' }}>
                      {pagination.total}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total Productos
                    </Typography>
                  </Box>
                  <Inventory2 sx={{ fontSize: 48, color: '#2196f3', opacity: 0.3 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ borderRadius: '16px', bgcolor: 'rgba(76, 175, 80, 0.1)' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: '#4caf50' }}>
                      {productos.filter(p => p.estado === 'activo').length}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Activos
                    </Typography>
                  </Box>
                  <CheckCircle sx={{ fontSize: 48, color: '#4caf50', opacity: 0.3 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ borderRadius: '16px', bgcolor: 'rgba(255, 152, 0, 0.1)' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: '#ff9800' }}>
                      {productos.filter(p => !p.registro_sanitario_id).length}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Descartables
                    </Typography>
                  </Box>
                  <Warning sx={{ fontSize: 48, color: '#ff9800', opacity: 0.3 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ borderRadius: '16px', bgcolor: 'rgba(156, 39, 176, 0.1)' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: '#9c27b0' }}>
                      {productos.filter(p => p.categoria === 'Controlado').length}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Controlados
                    </Typography>
                  </Box>
                  <FilterList sx={{ fontSize: 48, color: '#9c27b0', opacity: 0.3 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Barra de acciones */}
        <Card sx={{ borderRadius: '20px', mb: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
          <CardContent sx={{ p: 3 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  placeholder="Buscar por nombre, código SAP o laboratorio..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '12px',
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  select
                  fullWidth
                  label="Estado"
                  value={filtroEstado}
                  onChange={(e) => setFiltroEstado(e.target.value)}
                  SelectProps={{ native: true }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '12px',
                    },
                  }}
                >
                  <option value="">Todos</option>
                  <option value="activo">Activo</option>
                  <option value="inactivo">Inactivo</option>
                  <option value="descontinuado">Descontinuado</option>
                </TextField>
              </Grid>
              {canWrite() && (
                <Grid item xs={12} md={3}>
                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={<Add />}
                    onClick={() => handleOpenForm()}
                    sx={{
                      borderRadius: '12px',
                      py: 1.8,
                      textTransform: 'none',
                      fontWeight: 600,
                    }}
                  >
                    Nuevo Producto
                  </Button>
                </Grid>
              )}
            </Grid>
          </CardContent>
        </Card>

        {/* Tabla de productos */}
        <Card sx={{ borderRadius: '20px', boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'rgba(59, 130, 246, 0.05)' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Código SAP</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Nombre</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Descripción de la Presentación</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Laboratorio</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Registro Sanitario</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Estado</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {productos.map((producto) => (
                  <TableRow
                    key={producto.id}
                    sx={{
                      '&:hover': {
                        bgcolor: 'rgba(59, 130, 246, 0.02)',
                      },
                    }}
                  >
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {producto.codigo_sap}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {producto.nombre}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {producto.descripcion_presentacion || 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {producto.laboratorio_fabricante}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {producto.numero_registro_sanitario ? (
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {producto.numero_registro_sanitario}
                          </Typography>
                          <Chip
                            label={producto.estado_registro}
                            size="small"
                            color={producto.estado_registro === 'vigente' ? 'success' : 'warning'}
                            sx={{ mt: 0.5 }}
                          />
                        </Box>
                      ) : (
                        <Chip label="Descartable" size="small" color="warning" />
                      )}
                    </TableCell>
                    <TableCell>{getEstadoChip(producto.estado)}</TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => handleViewDetalle(producto)}
                        color="info"
                      >
                        <Visibility />
                      </IconButton>
                      {canWrite() && (
                        <IconButton
                          size="small"
                          onClick={() => handleOpenForm(producto)}
                          color="primary"
                        >
                          <Edit />
                        </IconButton>
                      )}
                      {canDelete() && (
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(producto.id)}
                          color="error"
                        >
                          <Delete />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Paginación */}
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <Button
              disabled={pagination.page === 1}
              onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
              sx={{ mx: 1 }}
            >
              Anterior
            </Button>
            <Typography sx={{ mx: 2, alignSelf: 'center' }}>
              Página {pagination.page} de {pagination.pages}
            </Typography>
            <Button
              disabled={pagination.page === pagination.pages}
              onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
              sx={{ mx: 1 }}
            >
              Siguiente
            </Button>
          </Box>
        </Card>

        {/* Diálogo de formulario */}
        <Dialog
          open={openForm}
          onClose={handleCloseForm}
          maxWidth="lg"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: '20px',
              maxHeight: '90vh',
            },
          }}
        >
          <ProductoForm
            producto={editingProducto}
            onClose={handleCloseForm}
          />
        </Dialog>

        {/* Diálogo de detalle */}
        <Dialog
          open={openDetalle}
          onClose={() => setOpenDetalle(false)}
          maxWidth="xl"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: '20px',
              maxHeight: '90vh',
            },
          }}
        >
          <ProductoDetalle
            producto={selectedProducto}
            onClose={() => {
              setOpenDetalle(false);
              loadProductos();
            }}
          />
        </Dialog>
      </motion.div>
    </Box>
  );
};

export default Productos;
